import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Grid, Avatar, CircularProgress } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarHalfIcon from '@material-ui/icons/StarHalf';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { useTranslation } from 'react-i18next';
import instance from '../../Config/axios';

const AVATAR_COLORS = ['#e05a2b', '#c0392b', '#2980b9', '#27ae60', '#8e44ad'];

const useStyles = makeStyles((theme) => ({
	section: {
		backgroundColor: '#fff',
		// padding: theme.spacing(8, 4),
	},
	title: {
		textAlign: 'center',
		fontWeight: 700,
		color: '#1a202c',
		marginBottom: theme.spacing(0.5),
		marginTop: theme.spacing(4),
	},
	subtitle: {
		textAlign: 'center',
		color: '#718096',
		fontSize: 14,
		marginBottom: theme.spacing(1),
	},
	orangeLine: {
		width: 48,
		height: 3,
		backgroundColor: '#e05a2b',
		borderRadius: 2,
		margin: '0 auto',
		marginBottom: theme.spacing(5),
	},
	overallBox: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		backgroundColor: '#f8fafc',
		border: '1px solid #e2e8f0',
		borderRadius: 16,
		padding: theme.spacing(4),
		maxWidth: 420,
		margin: '0 auto',
		marginBottom: theme.spacing(6),
	},
	bigRating: {
		fontSize: 52,
		fontWeight: 700,
		color: '#1a202c',
		lineHeight: 1,
	},
	reviewCount: {
		color: '#718096',
		fontSize: 13,
		marginTop: 4,
	},
	googleTag: {
		display: 'flex',
		alignItems: 'center',
		gap: 5,
		marginTop: 6,
	},
	card: {
		border: '1px solid #e2e8f0',
		borderRadius: 16,
		padding: theme.spacing(2.5),
		height: '100%',
		transition: 'all 0.25s ease',
		backgroundColor: '#fff',
		'&:hover': {
			borderColor: '#e05a2b',
			transform: 'translateY(-4px)',
			boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
		},
	},
	reviewerName: {
		fontSize: 14,
		fontWeight: 600,
		color: '#1a202c',
		textDecoration: 'none',
		'&:hover': { color: '#e05a2b' },
	},
	reviewDate: {
		fontSize: 12,
		color: '#a0aec0',
		marginTop: 2,
	},
	reviewText: {
		fontSize: 13,
		color: '#4a5568',
		lineHeight: 1.6,
		marginTop: theme.spacing(1),
		display: '-webkit-box',
		WebkitLineClamp: 4,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
	},
	ctaLink: {
		color: '#e05a2b',
		fontSize: 14,
		textDecoration: 'none',
		borderBottom: '1px solid rgba(224,90,43,0.35)',
		paddingBottom: 2,
	},
}));

function StarRating({ rating, size = 16 }) {
	return (
		<Box
			display='flex'
			style={{ gap: 2 }}>
			{[1, 2, 3, 4, 5].map((i) => {
				const style = {
					fontSize: size,
					color: rating >= i - 0.5 ? '#F5A623' : '#e2e8f0',
				};

				if (rating >= i) {
					return (
						<StarIcon
							key={i}
							style={style}
						/>
					);
				}

				if (rating >= i - 0.5) {
					return (
						<StarHalfIcon
							key={i}
							style={style}
						/>
					);
				}

				return (
					<StarBorderIcon
						key={i}
						style={{ ...style, color: '#e2e8f0' }}
					/>
				);
			})}
		</Box>
	);
}

function getInitials(name = '') {
	return name
		.split(' ')
		.map((n) => n[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();
}

function formatTime(timestamp, lang) {
	if (!timestamp) return '';
	return new Date(timestamp * 1000).toLocaleDateString(lang, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

export default function GoogleReviews() {
	const classes = useStyles();
	const { t, i18n } = useTranslation();

	const [data, setData] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState(null);

	React.useEffect(() => {
		instance
			.get('/users/reviews')
			.then(({ data }) => setData(data))
			.catch(() => setError(true))
			.finally(() => setLoading(false));
	}, []);

	return (
		<Box
			style={{
				maxWidth: '1200px',
				wordBreak: 'break-all',
				justifyContent: 'center',
				borderBottom: '1px solid rgb(227 200 200 / 50%)',
			}}
			className={classes.section}>
			<Typography
				variant='h4'
				className={classes.title}>
				{t('reviews_title')}
			</Typography>

			<Typography className={classes.subtitle}>{t('reviews_subtitle')}</Typography>

			<div className={classes.orangeLine} />

			{loading && (
				<Box
					display='flex'
					justifyContent='center'
					py={6}>
					<CircularProgress style={{ color: '#e05a2b' }} />
				</Box>
			)}

			{error && <Typography style={{ textAlign: 'center', color: '#718096' }}>{t('reviews_error')}</Typography>}

			{!loading && data && (
				<>
					{/* Overall rating */}
					<Box className={classes.overallBox}>
						<Typography className={classes.bigRating}>{Number(data.rating).toFixed(1)}</Typography>

						<Box>
							<StarRating
								rating={data.rating}
								size={20}
							/>

							<Typography className={classes.reviewCount}>{t('reviews_based_on', { count: data.totalReviews })}</Typography>

							<Box className={classes.googleTag}>
								<Typography style={{ color: '#e05a2b', fontWeight: 700, fontSize: 13 }}>G</Typography>
								<Typography style={{ color: '#a0aec0', fontSize: 12 }}>Google Reviews</Typography>
							</Box>
						</Box>
					</Box>

					{/* Reviews */}
					<Grid
						container
						justifyContent='center'
						style={{ padding: '10px' }}
						spacing={3}>
						{data.reviews.map((review, index) => (
							<Grid
								item
								xs={12}
								sm={6}
								md={4}
								key={index}>
								<Box className={classes.card}>
									<Box
										display='flex'
										alignItems='center'
										style={{ gap: 12, marginBottom: 12 }}>
										<Avatar
											src={review.profile_photo_url}
											style={{
												backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
												width: 40,
												height: 40,
												fontSize: 13,
											}}>
											{!review.profile_photo_url && getInitials(review.author)}
										</Avatar>

										<Box>
											<a
												href={review.author_url}
												target='_blank'
												rel='noopener noreferrer'
												className={classes.reviewerName}>
												{review.author}
											</a>

											<Typography className={classes.reviewDate}>{review.time}</Typography>
										</Box>
									</Box>

									<StarRating
										rating={review.rating}
										size={14}
									/>

									<Typography className={classes.reviewText}>{review.text}</Typography>
								</Box>
							</Grid>
						))}
					</Grid>

					<Box
						textAlign='center'
						mt={10}
						mb={10}>
						<a
							href={`https://search.google.com/local/reviews?placeid=${data.PLACE_ID}`}
							target='_blank'
							rel='noopener noreferrer'
							className={classes.ctaLink}>
							{t('reviews_view_all')}
						</a>
					</Box>
				</>
			)}
		</Box>
	);
}
